/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — Imperative toast API with auto-mount
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import type { ToastAPI, ToastOptions } from '../../types';
import type { ReactNode } from 'react';
import { toastStore } from './toastStore';
import { ToastContainer } from './ToastContainer';

const DEFAULT_DURATION = 3;

let _mounted = false;

function ensureMount(): void {
  if (_mounted || typeof document === 'undefined') return;
  _mounted = true;
  const el = document.createElement('div');
  el.id = 'ds-toast-root';
  document.body.appendChild(el);
  createRoot(el).render(React.createElement(ToastContainer));
}

function makeToastFn(type: 'success' | 'error' | 'warning' | 'info' | 'loading') {
  return (content: ReactNode, options: ToastOptions = {}): void => {
    ensureMount();
    toastStore.add({
      type,
      content,
      duration: options.duration ?? DEFAULT_DURATION,
      key: options.key,
    });
  };
}

export const toast: ToastAPI = {
  success: makeToastFn('success'),
  error: makeToastFn('error'),
  warning: makeToastFn('warning'),
  info: makeToastFn('info'),
  loading: makeToastFn('loading'),
  dismiss: (key?: string) => {
    if (key !== undefined) toastStore.remove(key);
    else toastStore.clear();
  },
};
